import { useState, useEffect } from "react";
import axios from "axios";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { toast } from "sonner";
import { CalendarIcon, Clock, Loader2 } from "lucide-react";
import { format, isSameDay } from "date-fns";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AppointmentModal({ 
  open, 
  onOpenChange, 
  sessionId, 
  actorId,
  userName,
  userPhone 
}) {
  const [services, setServices] = useState([]);
  const [loadingServices, setLoadingServices] = useState(true);
  const [serviceId, setServiceId] = useState("");
  const [date, setDate] = useState(null);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [time, setTime] = useState("");
  const [name, setName] = useState(userName || "");
  const [phone, setPhone] = useState(userPhone || "");
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const minDate = new Date();
  minDate.setHours(0, 0, 0, 0);

  // Fetch services when modal opens
  useEffect(() => {
    if (!open) return;
    
    const fetchServices = async () => {
      try {
        const res = await axios.get(`${API}/public/services`);
        setServices(res.data?.services || []);
      } catch (err) {
        console.error("Failed to fetch services:", err);
        toast.error("Failed to load services");
      } finally {
        setLoadingServices(false);
      }
    };
    
    fetchServices();
  }, [open]);

  // Fetch session info when modal opens
  useEffect(() => {
    if (!open || !sessionId || !actorId) return;
    
    const fetchSessionInfo = async () => {
      try {
        const res = await axios.get(`${API}/public/chat/session-info`, {
          params: { session_id: sessionId, actor_id: actorId },
        });
        if (res.data?.name && !name) setName(res.data.name);
        if (res.data?.phone && !phone) setPhone(res.data.phone);
      } catch (err) {
        console.warn("Could not fetch session info:", err);
      }
    };
    
    fetchSessionInfo();
  }, [open, sessionId, actorId]);

  // Fetch available slots when service and date are selected
  useEffect(() => {
    if (!open || !serviceId || !date) {
      setAvailableSlots([]);
      return;
    }
    
    const fetchSlots = async () => {
      setLoadingSlots(true);
      setAvailableSlots([]);
      setTime("");
      try {
        const res = await axios.get(`${API}/appointments-public/available-slots`, {
          params: {
            service_id: serviceId,
            date: format(date, "yyyy-MM-dd"),
          },
        });
        const slots = res.data?.data?.slots || [];
        const available = slots.filter((s) => s.available === true);
        setAvailableSlots(available);
        if (available.length === 0 && slots.length > 0) {
          toast.info("No available slots for this date. Please select another date.");
        }
      } catch (err) {
        console.error("Failed to fetch available slots:", err);
        toast.error("Failed to load available slots");
      } finally {
        setLoadingSlots(false);
      }
    };
    
    fetchSlots();
  }, [open, serviceId, date]);

  // Reset form when modal closes
  useEffect(() => {
    if (!open) {
      setServiceId("");
      setDate(null);
      setTime("");
      setNotes("");
      setAvailableSlots([]);
    }
  }, [open]);

  const getServiceLabel = (s) => s.name ?? s.title ?? s.service_name ?? "Service";
  const getServiceValue = (s) => s.id ?? s._id ?? s.service_id ?? getServiceLabel(s);

  const isSlotInPast = (selectedDate, slotTime) => {
    if (!selectedDate || !slotTime) return false;
    const now = new Date();
    if (!isSameDay(selectedDate, now)) return false;
    const [h, m] = slotTime.split(":").map(Number);
    const slotMinutes = (h || 0) * 60 + (m || 0);
    const nowMinutes = now.getHours() * 60 + now.getMinutes();
    return slotMinutes < nowMinutes;
  };

  const dateEnabled = !!serviceId;
  const timeEnabled = dateEnabled && !!date && !loadingSlots;
  const detailsEnabled = timeEnabled && !!time;

  const handleServiceChange = (val) => {
    const newVal = val === "__clear__" ? "" : val;
    setServiceId(newVal);
    if (!newVal) {
      setDate(null);
      setTime("");
    }
  };

  const handleDateChange = (val) => {
    setDate(val ?? null);
    setAvailableSlots([]);
    if (!val) {
      setTime("");
    }
  };

  const handleTimeChange = (val) => {
    setTime(val);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!serviceId) {
      toast.error("Please select a service first");
      return;
    }
    if (!date) {
      toast.error("Please select a date first");
      return;
    }
    if (!time) {
      toast.error("Please select a time first");
      return;
    }

    const selected = services.find((s) => getServiceValue(s) === serviceId);
    const serviceName = selected ? getServiceLabel(selected) : serviceId;
    const selectedSlot = availableSlots.find((s) => s.time === time);
    const duration = selectedSlot?.duration ?? 60;
    const dateStr = format(date, "yyyy-MM-dd");
    const payload = {
      service_id: serviceId,
      date: dateStr,
      time,
      duration,
      notes: notes || undefined,
      name: name || undefined,
      phone: phone || undefined,
      session_id: sessionId || undefined,
    };

    setSubmitting(true);
    try {
      await axios.post(`${API}/appointments-public`, payload);
      toast.success("Appointment submitted successfully");
      // Don't close modal here - wait for WebSocket event
    } catch (err) {
      const msg = err.response?.data?.detail || err.message || "Failed to submit appointment";
      toast.error(typeof msg === "string" ? msg : JSON.stringify(msg));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Booking Appointment</DialogTitle>
          <DialogDescription>
            Select service, date, and time.
          </DialogDescription>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-5">
          {/* Service */}
          <div className="space-y-2">
            <Label htmlFor="service">Service *</Label>
            <Select
              value={serviceId}
              onValueChange={handleServiceChange}
              disabled={loadingServices}
            >
              <SelectTrigger id="service">
                <SelectValue placeholder={loadingServices ? "Loading..." : "Select service"} />
              </SelectTrigger>
              <SelectContent>
                {serviceId && (
                  <SelectItem value="__clear__">— Clear selection —</SelectItem>
                )}
                {services.length === 0 && !loadingServices && (
                  <SelectItem value="__none__" disabled>
                    No services available
                  </SelectItem>
                )}
                {services.map((s) => {
                  const val = getServiceValue(s);
                  const label = getServiceLabel(s);
                  return (
                    <SelectItem key={val} value={val}>
                      {label}
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>
          </div>

          {/* Date */}
          <div className="space-y-2">
            <Label>Date *</Label>
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  type="button"
                  variant="outline"
                  className="w-full justify-start text-left font-normal"
                  disabled={!dateEnabled}
                >
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {date ? format(date, "dd MMMM yyyy") : "Select date"}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  mode="single"
                  selected={date}
                  onSelect={handleDateChange}
                  disabled={(d) => d < minDate}
                  initialFocus
                />
              </PopoverContent>
            </Popover>
          </div>

          {/* Time */}
          <div className="space-y-2">
            <Label>Time *</Label>
            {loadingSlots ? (
              <div className="flex items-center justify-center py-8 text-slate-500">
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Loading slots...
              </div>
            ) : !dateEnabled || !date ? (
              <div className="rounded-lg border border-slate-200 bg-slate-50 py-8 text-center text-sm text-slate-500">
                Select date first
              </div>
            ) : availableSlots.length === 0 ? (
              <div className="rounded-lg border border-slate-200 bg-slate-50 py-8 text-center text-sm text-slate-500">
                <p>No slots available for this date.</p>
                <p className="text-xs mt-1">Please select another date.</p>
              </div>
            ) : (
              <div className="grid max-h-48 grid-cols-2 gap-3 overflow-y-auto rounded-lg border border-slate-200 bg-slate-50/50 p-2">
                {availableSlots.map((slot) => {
                  const isSelected = time === slot.time;
                  const isPast = isSlotInPast(date, slot.time);
                  return (
                    <button
                      key={slot.time}
                      type="button"
                      onClick={() => !isPast && handleTimeChange(isSelected ? "" : slot.time)}
                      disabled={isPast}
                      className={`rounded-lg border-2 px-4 py-3 text-center text-sm font-medium transition-colors ${
                        isPast
                          ? "cursor-not-allowed border-slate-200 bg-slate-100 text-slate-400"
                          : isSelected
                            ? "border-indigo-600 text-indigo-600"
                            : "border-slate-200 bg-white text-slate-600 hover:border-slate-300 hover:bg-slate-50"
                      }`}
                    >
                      {slot.label ?? slot.time}
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {/* Optional: Name & Phone */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="name">Name</Label>
              <Input
                id="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Full name"
                disabled={!detailsEnabled}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="phone">Phone</Label>
              <Input
                id="phone"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="Phone number"
                disabled={!detailsEnabled}
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="notes">Notes (optional)</Label>
            <Textarea
              id="notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Additional information..."
              rows={2}
              disabled={!detailsEnabled}
            />
          </div>

          <Button type="submit" className="w-full" disabled={loadingServices || !detailsEnabled || submitting}>
            {submitting ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <Clock className="mr-2 h-4 w-4" />
            )}
            {submitting ? "Submitting..." : "Submit Appointment"}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
